import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, fmtMoney } from "@/components/transport/transportShared";
import { Truck, MapPin, PackageCheck, History, Loader2 } from "lucide-react";

/** Big full-width action button — the driver works one-handed on a phone. */
const ActionBtn = ({ label, onClick, busy, testId, primary }) => (
  <Button
    data-testid={testId}
    disabled={busy}
    onClick={onClick}
    className={`w-full py-6 text-base font-bold rounded-2xl ${primary ? "am-btn-primary" : ""}`}
    variant={primary ? "default" : "outline"}
  >
    {label}
  </Button>
);

export default function DriverTransport() {
  const [me, setMe] = useState(null);
  const [trips, setTrips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [countOpen, setCountOpen] = useState(false);
  const [count, setCount] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const refresh = useCallback(() => {
    api.get("/transport/driver/me").then((r) => setMe(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
    api.get("/transport/driver/trips").then((r) => setTrips(r.data.trips)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000); // live sync with admin/helper boards
    return () => clearInterval(id);
  }, [refresh]);

  const v = me?.vehicle;
  const trip = me?.trip;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post("/transport/driver/vehicle/status", { status });
      toast.success(status.replace(/_/g, " "));
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveCount = async () => {
    const n = parseInt(count, 10);
    setBusy(true);
    try {
      const r = await api.post("/transport/driver/vehicle/set-loaded", { count: n });
      toast.success(`Loaded ${r.data.loaded_animals} · remaining ${r.data.remaining_capacity}`);
      setCountOpen(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const n = parseInt(count, 10);
  const countValid = v && !isNaN(n) && n >= 0 && n <= v.animal_capacity;
  const s = v?.status;

  return (
    <DashboardShell title="My Trip" subtitle={me?.driver ? me.driver.name : ""}>
      <div className="max-w-xl mx-auto space-y-4" data-testid="driver-panel">
        {!me && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

        {me && !v && (
          <div className="am-card p-8 text-center text-muted-foreground" data-testid="driver-no-vehicle">
            <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No vehicle is assigned to you yet. Please contact your transporter or the market admin.
          </div>
        )}

        {v && (
          <>
            {/* Vehicle + capacity */}
            <div className="am-card p-5" data-testid="driver-vehicle-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">My Vehicle</div>
                  <div className="font-display text-2xl font-extrabold" data-testid="driver-vehicle-reg">{v.registration_number}</div>
                </div>
                <StatusBadge status={v.status} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4 text-center">
                {[["Capacity", v.animal_capacity, "driver-capacity"],
                  ["Loaded", v.loaded_animals, "driver-loaded"],
                  ["Remaining", v.remaining_capacity, "driver-remaining"]].map(([label, val, tid]) => (
                  <div key={label} className="rounded-2xl bg-muted py-3">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
                    <div className="font-display text-3xl font-extrabold" data-testid={tid}>{val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Current trip */}
            <div className="am-card p-5" data-testid="driver-trip-card">
              <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-2">Current Trip</div>
              {trip ? (
                <>
                  <div className="flex items-center gap-2 font-bold text-base">
                    <MapPin className="w-4 h-4 text-primary shrink-0" />
                    <span data-testid="driver-trip-route">{trip.pickup_area} → {trip.destination_pashu_haat}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {trip.customer_name} · {trip.animal_quantity} {trip.animal_type}
                    {trip.pickup_location ? ` · ${trip.pickup_location}` : ""}
                  </div>
                  <div className="mt-2"><StatusBadge status={trip.status} /></div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="driver-no-trip">No trip assigned right now. Wait for the market team to assign one.</p>
              )}
            </div>

            {/* Actions by status */}
            <div className="space-y-2.5" data-testid="driver-actions">
              {s === "ASSIGNED" && <ActionBtn primary label="START LOADING" testId="drv-start-loading" busy={busy} onClick={() => setStatus("LOADING")} />}
              {(s === "ASSIGNED" || s === "LOADING") && (
                <ActionBtn label="UPDATE ANIMAL COUNT" testId="drv-update-count" busy={busy}
                  onClick={() => { setCount(String(v.loaded_animals)); setCountOpen(true); }} />
              )}
              {s === "LOADING" && v.loaded_animals < v.animal_capacity && <ActionBtn label="MARK FULL / READY" testId="drv-mark-full" busy={busy} onClick={() => setStatus("FULL")} />}
              {(s === "LOADING" || s === "FULL") && <ActionBtn primary label="DEPART NOW" testId="drv-depart" busy={busy} onClick={() => setStatus("DEPARTED")} />}
              {s === "DEPARTED" && <ActionBtn primary label="REACHED PASHU HAAT" testId="drv-reached" busy={busy} onClick={() => setStatus("AT_PASHU_HAAT")} />}
              {s === "AT_PASHU_HAAT" && <ActionBtn primary label="COMPLETE TRIP" testId="drv-complete" busy={busy} onClick={() => setStatus("COMPLETED")} />}
              {(s === "AVAILABLE" || s === "COMPLETED" || s === "OFFLINE") && !trip && (
                <p className="text-center text-sm text-muted-foreground py-2">No actions right now.</p>
              )}
            </div>
          </>
        )}

        {/* Trip history */}
        <div className="am-card p-5" data-testid="driver-history">
          <button className="flex items-center gap-2 font-display text-lg font-bold w-full" data-testid="driver-history-toggle"
            onClick={() => setShowHistory(!showHistory)}>
            <History className="w-5 h-5 text-primary" /> My Completed Trips ({trips.length})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2.5">
              {trips.length === 0 && <p className="text-sm text-muted-foreground">No completed trips yet.</p>}
              {trips.map((t) => (
                <div key={t.trip_id} className="rounded-xl border border-border p-3 text-sm" data-testid={`driver-trip-${t.trip_id}`}>
                  <div className="flex justify-between gap-2 flex-wrap">
                    <span className="font-bold">{t.pickup} → {t.destination}</span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t.trip_id.slice(0, 8)} · {(t.date || "").slice(0, 10)} · {t.vehicle} · {t.animal_count} animals
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-center text-xs">
                    <div className="rounded-lg bg-muted py-1.5">Fare<br /><b>{fmtMoney(t.fare)}</b></div>
                    <div className="rounded-lg bg-muted py-1.5">Commission<br /><b>{fmtMoney(t.commission)}</b></div>
                    <div className="rounded-lg bg-emerald-50 py-1.5">Payable<br /><b className="text-emerald-700" data-testid={`driver-payable-${t.trip_id}`}>{fmtMoney(t.payable)}</b></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Count dialog */}
      <Dialog open={countOpen} onOpenChange={setCountOpen}>
        <DialogContent className="max-w-sm" data-testid="drv-count-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-primary" /> Animal Count</DialogTitle>
            <DialogDescription>Enter the number of animals currently loaded.</DialogDescription>
          </DialogHeader>
          {v && (
            <>
              <p className="text-sm text-muted-foreground">Capacity <b>{v.animal_capacity}</b> · currently loaded <b>{v.loaded_animals}</b></p>
              <Input type="number" min={0} max={v.animal_capacity} inputMode="numeric" value={count}
                className="am-input text-2xl font-bold text-center py-6" data-testid="drv-count-input"
                onChange={(e) => setCount(e.target.value)} />
              <p className={`text-sm font-semibold text-center ${countValid ? "text-muted-foreground" : "text-destructive"}`} data-testid="drv-count-hint">
                {countValid ? `Remaining will be ${v.animal_capacity - n}` : `Enter 0 – ${v.animal_capacity}`}
              </p>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCountOpen(false)}>Cancel</Button>
            <Button className="am-btn-primary" disabled={!countValid || busy} data-testid="drv-count-save" onClick={saveCount}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
